import { RefreshCw } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { refreshAll } from "../lib/refresh.ts";

/**
 * Pull down at the top of a screen to re-read it.
 *
 * The app deliberately has no elastic edge — nothing is behind the page, so a stretch was the
 * browser reporting travel that does not exist. This is the one pull that means something, and
 * it only exists at the top, only pulls one way, and only where the page is the thing that
 * scrolls.
 *
 * Three gates, because a pull that fires when it should not is worse than no pull at all:
 *
 *  - The page must own the scroll. The map takes it away (`body { overflow: hidden }`) because
 *    the map fills the screen, and there a downward drag is panning, not pulling. Asked of the
 *    computed style rather than tracked with a flag, so a screen that takes the scroll away is
 *    excluded by the fact of having done it.
 *  - Nothing may be open over the page. A sheet scrolls inside itself, and refreshing the page
 *    underneath it while someone is reading it is not what the gesture asks for.
 *  - The finger must not have started inside something that is itself scrolled down — a thread,
 *    a day strip — where the same drag means "back to the top of this".
 */

/** How far the indicator must travel before letting go means refresh. */
const TRIGGER_PX = 72;
/** The furthest it will travel, however hard the pull. */
const MAX_PX = 112;
/** Finger-to-indicator ratio: the pull is felt as weight rather than followed one to one. */
const RESISTANCE = 0.5;

export function PullToRefresh() {
  const [travel, setTravel] = useState(0);
  const [busy, setBusy] = useState(false);
  /** Gesture state lives in refs: a touchmove must not wait for a render to know where it was. */
  const from = useRef<number | null>(null);
  const now = useRef(0);
  const working = useRef(false);

  useEffect(() => {
    const scroller = () => document.scrollingElement ?? document.documentElement;

    /** The page is the thing that scrolls, and nothing is open over it. */
    const pageOwnsScroll = () => {
      // Both the long hand and the shorthand: a screen may set either, and an engine may
      // report only the one it was given.
      const style = getComputedStyle(document.body);
      if (style.overflowY === "hidden" || style.overflow === "hidden") return false;
      return document.querySelector('[role="dialog"], [role="alertdialog"]') === null;
    };

    /** Somewhere between the finger and the page, something is already scrolled down. */
    const insideScrolledContent = (target: EventTarget | null) => {
      let el = target instanceof Element ? target : null;
      while (el && el !== document.body) {
        if (el.scrollTop > 0) return true;
        el = el.parentElement;
      }
      return false;
    };

    const move = (to: number) => {
      now.current = to;
      setTravel(to);
    };

    const onStart = (e: TouchEvent) => {
      if (working.current || e.touches.length !== 1) return;
      if (scroller().scrollTop > 0) return;
      if (!pageOwnsScroll() || insideScrolledContent(e.target)) return;
      from.current = e.touches[0]!.clientY;
    };

    const onMove = (e: TouchEvent) => {
      if (from.current === null) return;
      // A second finger is a pinch or a map gesture, never this.
      if (e.touches.length !== 1) {
        from.current = null;
        move(0);
        return;
      }
      const dy = e.touches[0]!.clientY - from.current;
      if (dy <= 0) {
        // Pulling back up hands the gesture to the page, which is what it was going to do.
        from.current = null;
        move(0);
        return;
      }
      // Claim the gesture, which also replaces the browser's own pull-to-refresh with this one.
      if (e.cancelable) e.preventDefault();
      move(Math.min(MAX_PX, dy * RESISTANCE));
    };

    const onEnd = () => {
      if (from.current === null) return;
      from.current = null;
      if (now.current < TRIGGER_PX) {
        move(0);
        return;
      }
      // Held at the trigger while the work happens, so the spinner marks real waiting and the
      // indicator never springs back before the screen it promised to refresh has changed.
      working.current = true;
      setBusy(true);
      move(TRIGGER_PX);
      void refreshAll().finally(() => {
        working.current = false;
        setBusy(false);
        move(0);
      });
    };

    // Not passive: a pull at the top is this gesture, and the page must not scroll with it.
    document.addEventListener("touchstart", onStart, { passive: true });
    document.addEventListener("touchmove", onMove, { passive: false });
    document.addEventListener("touchend", onEnd);
    document.addEventListener("touchcancel", onEnd);
    return () => {
      document.removeEventListener("touchstart", onStart);
      document.removeEventListener("touchmove", onMove);
      document.removeEventListener("touchend", onEnd);
      document.removeEventListener("touchcancel", onEnd);
    };
  }, []);

  const armed = travel >= TRIGGER_PX;
  return (
    <>
      <div
        className="pull"
        data-dragging={from.current !== null}
        style={
          {
            "--pull": `${travel}px`,
            // Unitless as well as in pixels: an opacity cannot be a length, and neither can an
            // angle be one, so the fraction of the way to the trigger is given separately.
            "--pull-n": travel / TRIGGER_PX,
          } as React.CSSProperties
        }
        aria-hidden="true"
      >
        <span className="pull-mark" data-armed={armed} data-busy={busy}>
          <RefreshCw size={18} strokeWidth={2} />
        </span>
      </div>
      {/* The spinner is a picture; this is the part a screen reader is told. */}
      <p role="status" className="sr">
        {busy ? "Refreshing" : ""}
      </p>
    </>
  );
}
